// The miner page's derivations. Pure: the components receive finished numbers.
import type { MinerTask } from "@/lib/api";
import type { SweMinerSummary, SweTokenBreakdown } from "@/lib/api/types";
import { compareCategories } from "@/lib/dashboard/categories";
import type { Band } from "@/lib/dashboard/scale";

/** How much of the baseline was removed. Null when either side is unmeasured. */
export function reductionPct(baseline: number | null, miner: number | null): number | null {
  if (typeof baseline !== "number" || typeof miner !== "number" || baseline <= 0) return null;
  return (1 - miner / baseline) * 100;
}

export interface TokenCard {
  id: "weighted" | "input" | "cache" | "output";
  miner: number | null;
  baseline: number | null;
  /** Percent removed; null when there is nothing to compare. */
  saved: number | null;
}

/**
 * The four token figures, miner against baseline. Weighted is the one the score uses;
 * the other three say where the saving came from.
 */
export function tokenCards(t: SweTokenBreakdown): TokenCard[] {
  const pairs: [TokenCard["id"], number | null, number | null][] = [
    ["weighted", t.miner_weighted_tokens, t.baseline_weighted_tokens],
    ["input", t.miner_input_tokens, t.baseline_input_tokens],
    ["cache", t.miner_cached_input_tokens, t.baseline_cached_input_tokens],
    ["output", t.miner_output_tokens, t.baseline_output_tokens],
  ];
  return pairs.map(([id, miner, baseline]) => ({ id, miner, baseline, saved: reductionPct(baseline, miner) }));
}

export interface CategoryRow {
  key: string;
  score: number | null;
  total: number;
  /**
   * Tasks passed before and after compression. Null for a benchmark that has no
   * pass mark at all - explore scores continuously, and every one of its tasks reports
   * pass_without_compression as null, which "0/20 passed" would misreport as a failure.
   */
  passes: { baseline: number; compression: number } | null;
  /** Explore's own quality pair, averaged over the category's tasks, as ratios in [0,1]. */
  files: { hit: number; noise: number } | null;
}

/**
 * A category names either a difficulty (short/medium/long) or a benchmark
 * (swebench_verified, swe_explorer_*), and which one it is changes between competitions.
 * The keys of category_scores match `complexity` in the first case and `benchmark_type`
 * in the second, so a task belongs to a category when either field says so - matching on
 * complexity alone left every swe_* card without its counts.
 */
function inCategory(task: MinerTask, key: string): boolean {
  return task.complexity === key || task.benchmarkType === key;
}

function mean(values: number[]): number | null {
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Score per category, with how its tasks did. Counts come from the competition tasks
 * only - screener tasks are scored separately and mixing the two is what made the old
 * "tasks and tokens" section disagree with everything else.
 */
export function categoryRows(summary: SweMinerSummary | null, tasks: MinerTask[]): CategoryRow[] {
  const scores = summary?.category_scores ?? {};
  const keys = new Set<string>(Object.keys(scores));
  for (const task of tasks) {
    if (task.is_screener) continue;
    if (task.complexity) keys.add(task.complexity);
    else if (task.benchmarkType) keys.add(task.benchmarkType);
  }

  return [...keys].sort(compareCategories).map((key) => {
    const own = tasks.filter((t) => !t.is_screener && inCategory(t, key));
    // The pass mark is only real when the backend measured the uncompressed run too.
    // Explore reports pass_without_compression as null on every task while still filling
    // pass_with_compression, so testing either field showed it as "w/o 0/20" - a benchmark
    // that has no pass mark, reported as one the miner failed.
    const scored = own.some((t) => t.pass_without_compression !== null);
    const hit = mean(own.map((t) => t.hit_file_rate_avg).filter((v): v is number => typeof v === "number"));
    const noise = mean(own.map((t) => t.noise_file_rate_avg).filter((v): v is number => typeof v === "number"));

    return {
      key,
      score: scores[key] ?? null,
      total: own.length,
      passes: scored
        ? {
            baseline: own.filter((t) => t.pass_without_compression === true).length,
            compression: own.filter((t) => t.pass_with_compression === true).length,
          }
        : null,
      files: hit === null || noise === null ? null : { hit, noise },
    };
  });
}

export interface Meter {
  id: "without" | "with" | "hit" | "noise";
  /** Bar length, 0-100. */
  pct: number;
  value: string;
  /**
   * "ok" and "accent" only where the reading is a verdict: compression kept every pass it
   * had (or gained some), or it lost some. The baseline row is context, never coloured.
   */
  tone: "muted" | "ok" | "accent";
}

/**
 * A category's tasks as two short bars. With a pass mark: passes without compression,
 * then with it, out of the same total. Explore has none, so its pair is the file hit and
 * noise rates instead - hit is the good one, noise the cost.
 */
export function categoryMeters(row: CategoryRow): Meter[] {
  if (row.passes && row.total > 0) {
    const { baseline, compression } = row.passes;
    return [
      { id: "without", pct: sharePct(baseline, row.total), value: `${baseline}/${row.total}`, tone: "muted" },
      {
        id: "with",
        pct: sharePct(compression, row.total),
        value: `${compression}/${row.total}`,
        tone: compression >= baseline ? "ok" : "accent",
      },
    ];
  }
  if (row.files) {
    return [
      { id: "hit", pct: sharePct(row.files.hit, 1), value: `${(row.files.hit * 100).toFixed(1)}%`, tone: "ok" },
      { id: "noise", pct: sharePct(row.files.noise, 1), value: `${(row.files.noise * 100).toFixed(1)}%`, tone: "accent" },
    ];
  }
  return [];
}

export interface TaskCounts {
  total: number;
  competition: number;
  screener: number;
  stage1: number;
  stage2: number;
}

/**
 * How much work this miner actually did, counted off the task list itself. The summary
 * carries task_count and screener_task_count, but on CoT-Compression-8 they read 30 and
 * 10 against 90 tasks in the payload - a total smaller than its own competition half.
 * The list is the thing every other number on the page is derived from, so it decides
 * this one too (checked against production, 2026-09-23).
 */
export function taskCounts(tasks: MinerTask[]): TaskCounts {
  const screener = tasks.filter((t) => t.is_screener);
  return {
    total: tasks.length,
    competition: tasks.length - screener.length,
    screener: screener.length,
    stage1: screener.filter((t) => t.screener_stage !== 2).length,
    stage2: screener.filter((t) => t.screener_stage === 2).length,
  };
}

export type TaskGroupId = "competition" | "stage1" | "stage2";

export interface TaskGroup {
  id: TaskGroupId;
  tasks: MinerTask[];
}

/**
 * Which round of work: the competition itself, or one of the two screener stages. A
 * stage with no tasks is left out rather than shown as an empty tab.
 */
export function taskGroups(tasks: MinerTask[]): TaskGroup[] {
  const groups: TaskGroup[] = [
    { id: "competition", tasks: tasks.filter((t) => !t.is_screener) },
    { id: "stage1", tasks: tasks.filter((t) => t.is_screener && t.screener_stage !== 2) },
    { id: "stage2", tasks: tasks.filter((t) => t.is_screener && t.screener_stage === 2) },
  ];
  return groups.filter((g) => g.tasks.length > 0);
}

export interface BenchmarkFilter {
  key: string;
  count: number;
}

/**
 * Which benchmark, within whichever round is open. This is a second axis, not more of
 * the first: every round runs all three benchmarks, and the same task name appears once
 * per benchmark with an entirely different score and token count. Mixing the two into
 * one row of tabs made "Competition" and "edit" look like alternatives when one is a
 * slice of the other.
 *
 * Returned empty when there is nothing to choose between - one benchmark is not a filter.
 */
export function benchmarkFilters(tasks: MinerTask[]): BenchmarkFilter[] {
  const counts = new Map<string, number>();
  for (const task of tasks) {
    if (!task.benchmarkType) continue;
    counts.set(task.benchmarkType, (counts.get(task.benchmarkType) ?? 0) + 1);
  }
  if (counts.size < 2) return [];
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => compareCategories(a.key, b.key));
}

/** 126430000 -> "126.43M", 598000 -> "598k". Null stays "-". */
export function fmtCompact(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}

/** 25.12 -> "↓ 25.1%" (saved), -14.9 -> "↑ 14.9%" (used more). */
export function fmtSaved(saved: number): string {
  return `${saved >= 0 ? "↓" : "↑"} ${Math.abs(saved).toFixed(1)}%`;
}

/** "38/38", or "-" when the backend does not count runs for this benchmark. */
export function fmtRuns(passed: number | null, total: number | null): string {
  return typeof passed === "number" && typeof total === "number" ? `${passed}/${total}` : "-";
}

/** 148.5 -> "148.5s". */
export function fmtSeconds(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(1)}s` : "-";
}

/**
 * Where a rank sits in the field, on the four bands the scores use. The headline total
 * has no column to be compared against, and the production dashboard paints it green
 * whatever it says - which tells the 200th miner they did well. The standing is the one
 * honest scale available on this page, and it is already printed beside the number.
 */
export function rankBand(rank: number | null, total: number): Band {
  if (typeof rank !== "number" || !Number.isFinite(rank) || total <= 1) return "top";
  const share = (rank - 1) / (total - 1);
  if (share <= 0.25) return "top";
  if (share <= 0.5) return "high";
  return share <= 0.75 ? "mid" : "low";
}

/** `part` of `whole` as a percentage for a bar width, clamped to [0, 100]. 0 for no whole. */
export function sharePct(part: number, whole: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return 0;
  return Math.min(100, Math.max(0, (part / whole) * 100));
}

export interface TokenBar extends TokenCard {
  /** Miner as a share of baseline: 0.75 is a quarter saved, 1.15 is 15% more. */
  ratio: number | null;
  /** Bar length, as a percentage of the track. */
  fillPct: number;
  /** Where baseline sits on the track, as a percentage. 100 unless some bar overshoots it. */
  baselinePct: number;
  /** The part of the bar up to the baseline mark. */
  inkPct: number;
  /** The part past it - miner used more than baseline. 0 when it did not. */
  overPct: number;
}

/**
 * Miner against baseline as bars on one shared scale: the track is the baseline, the bar
 * is what the miner used. Every row is read as a share of its own baseline, so input
 * (tens of millions) and cache (over a hundred) compare as savings, not as sizes. When a
 * row went over baseline the scale stretches to fit it and the baseline becomes a mark
 * short of the end, so the overshoot is visible rather than clipped.
 */
export function tokenBars(t: SweTokenBreakdown): TokenBar[] {
  const cards = tokenCards(t);
  const ratios = cards.map((c) =>
    typeof c.miner === "number" && typeof c.baseline === "number" && c.baseline > 0 ? c.miner / c.baseline : null,
  );
  const scaleMax = Math.max(1, ...ratios.filter((r): r is number => r !== null));
  return cards.map((card, i) => {
    const ratio = ratios[i];
    const fillPct = ratio === null ? 0 : sharePct(ratio, scaleMax);
    const baselinePct = sharePct(1, scaleMax);
    return {
      ...card,
      ratio,
      fillPct,
      baselinePct,
      inkPct: Math.min(fillPct, baselinePct),
      overPct: Math.max(0, fillPct - baselinePct),
    };
  });
}

export type TaskSortKey = "default" | "score" | "tokens";
export type TaskSortDirection = "asc" | "desc";

/**
 * The task list in the backend's order, or by score or by tokens. Missing values go last
 * in both directions - "not measured" is not "worst".
 */
export function sortTasks(tasks: MinerTask[], key: TaskSortKey, direction: TaskSortDirection): MinerTask[] {
  if (key === "default") return tasks;
  const value = (t: MinerTask) => (key === "score" ? t.platform_score : t.tokens_with_compression);
  const sign = direction === "asc" ? 1 : -1;
  return [...tasks].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return (va - vb) * sign;
  });
}

/** A row's identity in the task table: the same task id repeats across benchmarks. */
export function taskKey(task: MinerTask): string {
  return `${task.benchmarkType ?? "na"}:${task.task_id}`;
}
